import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useLang } from "../context/LanguageContext";
import { Zap, Mail, QrCode, Image as ImageIcon, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";

export default function CreditsPanel() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [state, setState] = useState(null);

  useEffect(() => {
    api.get("/credits/me").then((r) => setState(r.data)).catch(() => {});
  }, []);

  if (!state) return null;

  const used = Math.max(state.allowance - state.balance, 0);
  const pct = state.allowance > 0 ? Math.min(100, (used / state.allowance) * 100) : 0;
  const low = state.balance < state.allowance * 0.2;

  const rows = [
    { icon: Mail, label: t("credit_cost_email"), n: state.cost_email_per_platform, color: "#2B8C9F" },
    { icon: QrCode, label: t("credit_cost_qr"), n: state.cost_qr_per_platform, color: "#0A2540" },
    { icon: ImageIcon, label: t("credit_cost_photos"), n: state.cost_photos_per_platform, color: "#E57C4F" },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="credits-panel">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-[#E57C4F]/10 grid place-items-center">
            <Zap className="w-5 h-5 text-[#E57C4F]" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{t("credits_title")}</div>
            <div className="font-display font-bold text-3xl text-[#0A2540] mt-0.5" data-testid="credits-balance">
              {state.balance} <span className="text-slate-400 text-lg font-medium">/ {state.allowance}</span>
            </div>
          </div>
        </div>
        {!state.has_subscription && (
          <Button size="sm" onClick={() => navigate("/#pricing")} className="rounded-full bg-[#0A2540] hover:bg-[#0A2540]/90 text-white gap-1" data-testid="credits-upgrade-btn">
            {t("credits_upgrade")} <ArrowRight className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      <div className="h-2 rounded-full bg-slate-100 overflow-hidden" data-testid="credits-bar">
        <div className={`h-full rounded-full transition-all ${low ? "bg-[#E57C4F]" : "bg-[#10B981]"}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 text-xs text-slate-500">
        {t("credits_used_of", { used, total: state.allowance })}
        {state.reset_at && <> · {t("credits_reset", { date: new Date(state.reset_at).toLocaleDateString() })}</>}
      </div>

      <div className="mt-6 pt-5 border-t border-slate-100">
        <div className="font-display font-semibold text-slate-800 mb-1">{t("how_credits_title")}</div>
        <p className="text-xs text-slate-500 mb-4">{t("how_credits_desc")}</p>
        <ul className="space-y-2.5" data-testid="credits-costs-list">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <r.icon className="w-4 h-4" style={{ color: r.color }} /> {r.label}
              </span>
              <span className="font-semibold text-slate-800">{t("credit_per_platform", { n: r.n })}</span>
            </li>
          ))}
        </ul>
        <div className="mt-4 text-xs text-slate-500 italic">{t("credit_formula")}</div>
      </div>
    </div>
  );
}
